/** @jsxImportSource @emotion/react */
import { Box, FlexLayout } from '@deity-io/portal-ui';
import type { CSSObject } from '@emotion/react';
import type { ReactElement, ReactNode } from 'react';
import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { Tab } from './Tab';

interface ITabsProps {
  style?: CSSObject;
  children: ReactNode[] | ReactNode;
  maxWidth?: number;
  badge?: ReactNode[] | ReactNode;
  onChange?: (tab: string) => boolean | undefined | void;
}
export const Tabs: React.FC<ITabsProps> = props => {
  const [activeTab, setActiveTabState] = useState();
  /**
   * set the active tab
   * @param tab the React Element
   * @param force todo : implement, cancelableCallBack, and ignore result with this flag
   * @param silent dont raise change event
   */
  const setActiveTab = (tab: any, force = false, silent = false) => {
    if (!silent && props.onChange) {
      const cancel = props.onChange(tab);
      if (force || cancel !== true) {
        setActiveTabState(tab);
      }
    } else {
      setActiveTabState(tab);
    }
  };

  /**
   * todo, remove this tabindex.
   */
  const [activeTabIndex, setTabIndex] = useState(0);
  const tabNavRef = useRef<HTMLElement>(null);
  const children = React.Children.toArray(props.children);
  const childRefs: any = useMemo(() => {
    if (children.length) {
      return children.map(() => createRef());
    }

    return props.children;
  }, []);

  useEffect(() => {
    if (activeTab === undefined) {
      let activeTabindex: number = 0;
      const tabToActivate = React.Children.map(props.children, (child, index) => {
        if (React.isValidElement(child)) {
          if (child.props.isActive) {
            activeTabindex = index;

            return child.props.label;
          }
        }
      });
      if (tabToActivate) {
        if (activeTab !== tabToActivate[0]) {
          setActiveTab(tabToActivate[0], true, true);
          setTabIndex(activeTabindex);
        }
      }
    }
  }, [props]);

  const tabItemsWithProps = React.Children.map(props.children, child => {
    if (React.isValidElement(child)) {
      if (child.props.label === activeTab) {
        return React.cloneElement(child as ReactElement<any>, { isActive: true });
      }

      return React.cloneElement(child as ReactElement<any>, { isActive: false });
    }

    return child;
  });

  return (
    <div
      css={{
        position: 'relative',
        width: '100%',
        height: '100%',
        maxWidth: props.maxWidth ? `${props.maxWidth}px` : undefined
      }}
    >
      <FlexLayout flexDirection="row">
        <nav css={{ padding: `${props.style?.padding || 0}`, marginLeft: '2em' }} ref={tabNavRef}>
          {children.map((child: any, i) => (
            <Tab
              key={child.key}
              ref={childRefs[i]}
              label={child.props.label}
              disabled={child.props.disabled}
              isActive={child.props.label === activeTab}
              setActiveTab={() => setActiveTab(child.props.label)}
              setTabIndex={() => setTabIndex(i)}
            />
          ))}
        </nav>

        {props.badge && <Box css={{ marginLeft: 'auto' }}>{props.badge}</Box>}
      </FlexLayout>
      <div
        style={{
          position: 'absolute',
          width: childRefs[activeTabIndex]?.current?.offsetWidth,
          top: tabNavRef?.current?.clientHeight,
          left: childRefs[activeTabIndex]?.current?.offsetLeft
        }}
        css={styles.tabIndicator}
      />
      <div style={{ marginTop: '4px', paddingBottom: '2em', height: '100%' }} css={styles.tabContent}>
        {tabItemsWithProps}
      </div>
    </div>
  );
};

const styles: CSSObject = {
  tabs: {},
  tabHeaders: {
    display: 'flex'
  },
  tabIndicator: {
    height: '4px',
    background: '#6057D3',
    borderRadius: '4px 4px 0px 0px',
    transition: 'all .2s ease-in-out'
  },
  tabContent: {
    height: '100%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0E2E5',
    boxSizing: 'border-box',
    boxShadow: ' 0px 10px 30px rgba(30, 24, 60, 0.1)',
    borderRadius: '8px'
  }
};
